/**
 * Commitment Rebalancer — public continuous-rebalance dashboard
 * ===============================================================
 * Closes Vantage's "one-shot SP/RI" gap. Lives at /commitment-rebalancer.
 * Sibling of /commitment-coach (single-shot CFO calculator).
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import SEOHead from '../components/SEOHead';
import SpendBreakdownChart from '../components/SpendBreakdownChart';
import {
  ArrowLeft, ArrowRight, Activity, TrendingUp, TrendingDown,
  AlertTriangle, ShieldCheck, Loader2, RefreshCw, GitCompare,
  Upload, CheckCircle2, Database,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '$0';
  const a = Math.abs(n); const sign = n < 0 ? '-' : '';
  if (a >= 1_000_000) return `${sign}$${(a / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `${sign}$${(a / 1_000).toFixed(0)}K`;
  return `${sign}$${a.toFixed(0)}`;
};

const PROVIDERS = [
  { key: 'aws',   label: 'AWS · Compute SP',  hint: '28% / 1y · 47% / 3y' },
  { key: 'azure', label: 'Azure · RI',        hint: '30% / 1y · 55% / 3y' },
  { key: 'gcp',   label: 'GCP · CUD',         hint: '25% / 1y · 55% / 3y' },
];

const CommitmentRebalancer = () => {
  const [spend, setSpend] = useState(150_000);
  const [coverage, setCoverage] = useState(35);
  const [provider, setProvider] = useState('aws');
  const [proposed, setProposed] = useState(50);
  const [position, setPosition] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [simLoading, setSimLoading] = useState(false);
  // Authed real-curve hooks
  const [curveStatus, setCurveStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const isAuthed = !!token;

  const fetchCurveStatus = async () => {
    if (!isAuthed) return;
    try {
      const { data } = await axios.get(`${API_URL}/commitment-rebalancer/spend-curve/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCurveStatus(data);
    } catch (e) { /* not admin/authed — silent */ }
  };

  const uploadCsv = async (file, source = 'aws-cur') => {
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(
        `${API_URL}/commitment-rebalancer/ingest-spend/csv?source=${source}`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } },
      );
      setUploadResult({ ok: true, ...data });
      await fetchCurveStatus();
      await fetchPosition();
    } catch (e) {
      setUploadResult({ ok: false, error: e?.response?.data?.detail || e?.message || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  const fetchPosition = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/commitment-rebalancer/current-position`, {
        params: {
          monthly_compute_spend_usd: spend,
          current_coverage_pct: coverage,
          provider,
          term: '1y',
        },
      });
      setPosition(data);
      setProposed(data.recommended_30d_sweet_pct);
    } finally {
      setLoading(false);
    }
  };

  const simulate = async () => {
    setSimLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/commitment-rebalancer/simulate-rebalance`, {
        monthly_compute_spend_usd: spend,
        current_coverage_pct: coverage,
        proposed_coverage_pct: proposed,
        provider,
        term: '1y',
      });
      setSimulation(data);
    } finally {
      setSimLoading(false);
    }
  };

  useEffect(() => { fetchPosition(); fetchCurveStatus(); /* eslint-disable-next-line */ }, []);

  const driftTone = useMemo(() => {
    if (!position) return 'slate';
    if (position.drift_severity === 'high') return 'rose';
    if (position.drift_severity === 'medium') return 'amber';
    return 'emerald';
  }, [position]);

  return (
    <div className="relative min-h-screen bg-white text-slate-900 overflow-hidden">
      <SEOHead
        title="Continuous Commitment Rebalancer · CARTIEAI"
        description="Vantage gives you a one-shot recommendation. We give you a 30/60/90 rolling window — and a drift alert when your posture has wandered out of band."
        url="https://cartieai.com/commitment-rebalancer"
      />

      {/* Brand atmosphere orbs */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-10 left-1/4 w-[420px] h-[420px] rounded-full bg-emerald-200/30 blur-[120px]" />
        <div className="absolute top-32 right-1/4 w-[380px] h-[380px] rounded-full bg-emerald-200/30 blur-[120px]" />
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12 relative">
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-emerald-700 font-medium" data-testid="rebalancer-home-link">
            <ArrowLeft className="w-4 h-4" /> Back to CARTIE
          </Link>
        </div>

        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 text-amber-300 mb-4 text-[11px] font-mono uppercase tracking-[0.18em] shadow-lg shadow-slate-900/20 border border-slate-800">
            <ShieldCheck className="w-3.5 h-3.5" /> Proprietary engine · Public beta
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-4 tracking-tight leading-[1.05]" data-testid="rebalancer-title">
            Continuous{' '}
            <span className="text-emerald-600">Commitment Rebalancer</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-700 max-w-3xl font-medium leading-relaxed">
            Vantage and Apptio give you one big recommendation, then move on.
            Real workloads drift. We watch your CV across rolling 30/60/90-day
            windows and flag when your SP/RI posture has wandered out of band.
            <span className="block mt-3 text-sm text-slate-600">
              All math is shown. <Link to="/commitment-coach" className="text-emerald-700 underline-offset-2 hover:underline font-semibold">/commitment-coach</Link> is the one-shot version.
            </span>
          </p>
        </div>

        {/* Inputs */}
        <Card className="border-slate-200 bg-white mb-6 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-slate-900"><Activity className="w-5 h-5 text-emerald-600" /> Your position</CardTitle>
            <CardDescription className="text-slate-600">Nothing leaves the browser. Math runs server-side on transient inputs.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">Monthly compute spend (USD)</label>
                <Input
                  type="number"
                  value={spend}
                  onChange={(e) => setSpend(Math.max(0, Number(e.target.value) || 0))}
                  className="border-slate-300 bg-white text-slate-900"
                  data-testid="rebalancer-spend-input"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">Current coverage %</label>
                <Input
                  type="number"
                  value={coverage}
                  onChange={(e) => setCoverage(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                  className="border-slate-300 bg-white text-slate-900"
                  data-testid="rebalancer-coverage-input"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">Provider</label>
                <select
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-slate-900 text-sm"
                  data-testid="rebalancer-provider-select"
                >
                  {PROVIDERS.map((p) => (<option key={p.key} value={p.key}>{p.label}</option>))}
                </select>
              </div>
              <div className="flex items-end">
                <Button className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold transition-all font-semibold shadow-md shadow-emerald-500/20" onClick={fetchPosition} disabled={loading} data-testid="rebalancer-refresh-btn">
                  {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…</>) : (<><RefreshCw className="w-4 h-4 mr-2" /> Re-analyze</>)}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rolling windows */}
        {position && (
          <div className="grid gap-4 md:grid-cols-3 mb-6">
            {position.windows.map((w) => (
              <Card key={w.label} className="border-slate-200 bg-white shadow-sm" data-testid={`rebalancer-window-${w.days}`}>
                <CardHeader className="pb-2">
                  <CardDescription className="text-slate-600 font-medium">{w.label} rolling window</CardDescription>
                  <CardTitle className="text-2xl tabular-nums text-slate-900 font-extrabold">{w.sweet_spot}%</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-xs text-slate-700 font-medium">
                    Sweet-spot · band {w.band_low}–{w.band_high}%
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    Workload CV: {w.cv_pct}% · {w.cv_pct < 15 ? 'stable' : w.cv_pct < 30 ? 'mixed' : 'volatile'}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Drift verdict */}
        {position && (
          <Card className={`border-${driftTone}-300 bg-${driftTone}-50 mb-6`} data-testid="rebalancer-drift-card">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="shrink-0">
                  {position.drift_severity === 'high' ? (
                    <AlertTriangle className={`w-8 h-8 text-${driftTone}-600`} />
                  ) : position.drift_severity === 'low' ? (
                    <ShieldCheck className={`w-8 h-8 text-${driftTone}-600`} />
                  ) : (
                    <TrendingUp className={`w-8 h-8 text-${driftTone}-600`} />
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900 text-lg">{position.verdict.headline}</div>
                  <div className="text-sm text-slate-700 mt-1">
                    30-day window drift vs 90-day baseline:{' '}
                    <span className="font-medium tabular-nums">{position.drift_pp_30d_vs_90d > 0 ? '+' : ''}{position.drift_pp_30d_vs_90d} pp</span>
                    {' · '}{position.drift_severity} severity
                  </div>
                  <div className="text-sm text-slate-700 mt-2">
                    Annual savings opportunity at the 30-day sweet spot:{' '}
                    <span className="font-semibold tabular-nums">{fmtMoney(position.annual_savings_delta_usd)}/yr</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Simulate a rebalance */}
        <Card className="border-slate-200 bg-white mb-6 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-slate-900"><GitCompare className="w-5 h-5 text-emerald-600" /> Simulate a rebalance</CardTitle>
            <CardDescription className="text-slate-600">What if you moved coverage to the proposed %? Stateless what-if — no buy executed.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-3 items-end">
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">Proposed coverage %</label>
                <Input
                  type="number"
                  value={proposed}
                  onChange={(e) => setProposed(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                  className="border-slate-300 bg-white text-slate-900"
                  data-testid="rebalancer-proposed-input"
                />
              </div>
              <div>
                <Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold transition-all font-semibold shadow-md shadow-emerald-500/20" onClick={simulate} disabled={simLoading} data-testid="rebalancer-simulate-btn">
                  {simLoading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Simulating…</>) : (<>Simulate <ArrowRight className="w-4 h-4 ml-2" /></>)}
                </Button>
              </div>
            </div>
            {simulation && (
              <div className="mt-4 p-4 rounded-md border border-emerald-300 bg-emerald-50 text-sm" data-testid="rebalancer-sim-result">
                <div className="font-semibold text-emerald-800">{simulation.narrative}</div>
                <div className="mt-2 grid grid-cols-3 gap-3 text-xs">
                  <div>
                    <div className="text-slate-600 font-medium">At current</div>
                    <div className="tabular-nums font-semibold text-slate-900">{fmtMoney(simulation.annual_savings_at_current_usd)}/yr saved</div>
                  </div>
                  <div>
                    <div className="text-slate-600 font-medium">At proposed</div>
                    <div className="tabular-nums font-semibold text-slate-900">{fmtMoney(simulation.annual_savings_at_proposed_usd)}/yr saved</div>
                  </div>
                  <div>
                    <div className="text-slate-600 font-medium">Delta</div>
                    <div className={`tabular-nums font-bold ${simulation.annual_savings_delta_usd >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {simulation.annual_savings_delta_usd >= 0 ? '+' : ''}{fmtMoney(simulation.annual_savings_delta_usd)}/yr
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stacked bar chart — visual breakdown when authed and real data exists */}
        {isAuthed && curveStatus && !curveStatus.is_synthetic && (
          <Card className="border-emerald-500/20 mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2"><Activity className="w-5 h-5 text-emerald-400" /> Where your spend really goes</CardTitle>
              <CardDescription className="text-slate-400">Stacked bar per day · only the green (compute) layer counts toward SP/RI math.</CardDescription>
            </CardHeader>
            <CardContent>
              <SpendBreakdownChart days={30} />
            </CardContent>
          </Card>
        )}

        {/* CSV Upload (real curve mode — authed only) */}
        {isAuthed && (
          <Card className="border-emerald-500/30 mb-6" data-testid="rebalancer-upload-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Database className="w-5 h-5 text-emerald-400" /> Switch to real-data mode</CardTitle>
              <CardDescription className="text-slate-400">
                Upload an AWS CUR, Azure Cost Management, or GCP Billing Export CSV.
                30+ days unlocks the real-curve path (replaces the synthetic baseline).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-3 items-end">
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-slate-700 mb-1 block">CSV file (date column + cost column)</label>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => uploadCsv(e.target.files?.[0], provider === 'azure' ? 'azure-cm' : provider === 'gcp' ? 'gcp-billing' : 'aws-cur')}
                    disabled={uploading}
                    className="block w-full text-sm text-slate-200 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:bg-emerald-600 file:text-white hover:file:bg-emerald-700 file:cursor-pointer"
                    data-testid="rebalancer-csv-input"
                  />
                </div>
                <div className="text-xs text-slate-600 leading-tight">
                  {uploading ? (
                    <span className="inline-flex items-center gap-1 text-emerald-300"><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</span>
                  ) : curveStatus && !curveStatus.is_synthetic ? (
                    <span className="inline-flex items-center gap-1 text-emerald-300" data-testid="curve-real-badge">
                      <CheckCircle2 className="w-4 h-4" /> Real curve · {curveStatus.points_available} days
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-slate-600" data-testid="curve-synthetic-badge">
                      <AlertTriangle className="w-4 h-4 text-amber-400" /> Synthetic curve · upload CSV to switch
                    </span>
                  )}
                </div>
              </div>
              {uploadResult && (
                <div className={`mt-3 text-xs p-2 rounded ${uploadResult.ok ? 'bg-emerald-500/10 text-emerald-100 border border-emerald-500/30' : 'bg-rose-500/10 text-rose-200 border border-rose-500/30'}`} data-testid="rebalancer-upload-result">
                  {uploadResult.ok ? (
                    <>Parsed <b className="tabular-nums">{uploadResult.rows_parsed}</b> rows · ingested <b className="tabular-nums">{uploadResult.days_ingested}</b> days · source <code>{uploadResult.curve_source?.source}</code>. The 30/60/90 windows above are now LIVE.</>
                  ) : (
                    <>Upload error: {uploadResult.error}</>
                  )}
                </div>
              )}
              {curveStatus?.category_30d_breakdown_usd && Object.keys(curveStatus.category_30d_breakdown_usd).length > 0 && (
                <div className="mt-3" data-testid="rebalancer-category-breakdown">
                  <div className="text-xs text-slate-600 mb-1.5">30-day spend by category (only <b>compute</b> counts toward SP/RI math):</div>
                  <div className="flex gap-2 flex-wrap">
                    {Object.entries(curveStatus.category_30d_breakdown_usd).map(([cat, amt]) => {
                      const color = { compute: 'emerald', storage: 'cyan', egress: 'amber', other: 'slate' }[cat] || 'slate';
                      return (
                        <div key={cat} className={`px-2 py-1 rounded-md bg-${color}-50 border border-${color}-200 text-xs`}>
                          <span className={`font-medium text-${color}-900`}>{cat}</span>{' '}
                          <span className="tabular-nums text-slate-700">${Math.round(amt).toLocaleString()}</span>
                        </div>
                      );
                    })}
                  </div>
                  <Link to="/admin/cost-actuals" className="text-xs text-emerald-300 hover:underline mt-2 inline-flex items-center gap-1" data-testid="link-to-cost-actuals">
                    View / edit every ingested point →
                  </Link>
                </div>
              )}
              <div className="mt-3 text-[11px] text-slate-600">
                Accepted headers (case-insensitive): <code>date | usagedate | usage_start_time | lineItem/UsageStartDate</code> + <code>amount_usd | cost | costinbillingcurrency | lineItem/UnblendedCost</code>.
                Daily totals are aggregated automatically from hourly rows. Negative refund lines are skipped.
                For automatic compute/storage/egress classification, include a service column (<code>lineItem/ProductCode</code>, <code>MeterCategory</code>, <code>service.description</code>).
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-emerald-500/20 bg-slate-900 text-slate-100">
          <CardContent className="pt-6 text-sm text-slate-300">
            <strong className="text-white">Honest Beta:</strong> the 90-day curve is deterministically synthesized
            from your monthly spend until you connect your CUR / Azure Cost Mgmt / GCP Billing Export feed.
            The math, discount table, and drift logic are identical to the production code paths.
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CommitmentRebalancer;
